import { Controller } from "@hotwired/stimulus"

import { trackPreviewPlay } from "@/lib/ahoy"
import { formatTime } from "@/lib/utils"

// Connects to data-controller="preview"
// Handles preview playback with play/pause/replay states and fade-out
export default class extends Controller {
  static targets = [
    "button",
    "icon",
    "text",
    "previewContainer",
    "playingState",
    "playPauseButton",
    "playPauseIcon",
    "progressRing",
    "currentTime",
    "totalTime",
    "timeDisplay",
    "muxAudioTemplate",
    "audioTemplate",
  ]
  static values = {
    muxPlaybackId: String,
    muxPlaybackToken: String,
    src: String,
    previewDuration: Number,
    itemId: String,
    spaceId: String,
    replayIcon: { type: String, default: "refresh-cw" },
  }

  declare readonly hasButtonTarget: boolean
  declare readonly buttonTarget: HTMLButtonElement
  declare readonly hasIconTarget: boolean
  declare readonly iconTarget: HTMLElement
  declare readonly hasTextTarget: boolean
  declare readonly textTarget: HTMLElement
  declare readonly hasPreviewContainerTarget: boolean
  declare readonly previewContainerTarget: HTMLElement
  declare readonly hasPlayingStateTarget: boolean
  declare readonly playingStateTarget: HTMLElement
  declare readonly hasPlayPauseButtonTarget: boolean
  declare readonly playPauseButtonTarget: HTMLButtonElement
  declare readonly hasPlayPauseIconTarget: boolean
  declare readonly playPauseIconTarget: HTMLElement
  declare readonly hasProgressRingTarget: boolean
  declare readonly progressRingTarget: HTMLElement & { value: number }
  declare readonly hasCurrentTimeTarget: boolean
  declare readonly currentTimeTarget: HTMLElement
  declare readonly hasTotalTimeTarget: boolean
  declare readonly totalTimeTarget: HTMLElement
  declare readonly hasTimeDisplayTarget: boolean
  declare readonly timeDisplayTarget: HTMLElement
  declare readonly hasMuxAudioTemplateTarget: boolean
  declare readonly muxAudioTemplateTarget: HTMLTemplateElement
  declare readonly hasAudioTemplateTarget: boolean
  declare readonly audioTemplateTarget: HTMLTemplateElement
  declare muxPlaybackIdValue: string
  declare muxPlaybackTokenValue: string
  declare srcValue: string
  declare previewDurationValue: number
  declare itemIdValue: string
  declare spaceIdValue: string
  declare replayIconValue: string

  private mediaElement: HTMLElement | HTMLAudioElement | null = null
  private container: HTMLDivElement | null = null
  private state: "idle" | "playing" | "completed" = "idle"
  private fadeIntervalId: ReturnType<typeof setInterval> | null = null
  private timeUpdateIntervalId: ReturnType<typeof setInterval> | null = null
  private hasTrackedPreview = false

  connect() {
    this.initializeMediaElement()
    this.updateButtonState()
    // Reset tracking flag when controller connects (new page load)
    this.hasTrackedPreview = false
  }

  disconnect() {
    this.cleanup()
  }

  // An embed card stops its clip when the listener may play the full
  // recording, so the two never play over each other (#2242).
  stop() {
    this.pause()
  }

  toggle() {
    if (this.state === "idle" || this.state === "completed") {
      this.play()
    } else if (this.state === "playing") {
      this.pause()
    }
  }

  private play() {
    if (!this.mediaElement) return

    // Reset to beginning if completed
    if (this.state === "completed") {
      if (this.mediaElement instanceof HTMLAudioElement) {
        this.mediaElement.currentTime = 0
        this.mediaElement.volume = 1 // Reset volume for HTML5 audio
      } else {
        // For mux-audio, try to reset currentTime and volume
        const muxAudio = this.mediaElement as {
          currentTime?: number
          media?: HTMLAudioElement
          volume?: number
        }
        if (muxAudio.media) {
          muxAudio.media.currentTime = 0
          muxAudio.media.volume = 1 // Reset volume via media property
        } else if ("currentTime" in muxAudio) {
          muxAudio.currentTime = 0
          // Try to reset volume directly on mux-audio if available
          if ("volume" in muxAudio) {
            muxAudio.volume = 1
          }
        }
      }
      // Reset progress and time display
      this.updateProgress(0)
      this.updateTimeDisplay(0)
    }

    // Play the media
    if (this.mediaElement instanceof HTMLAudioElement) {
      void this.mediaElement.play()
    } else if (
      this.mediaElement &&
      "play" in this.mediaElement &&
      typeof (this.mediaElement as { play?: () => Promise<void> }).play ===
        "function"
    ) {
      void (this.mediaElement as { play: () => Promise<void> }).play()
    }
  }

  private pause() {
    if (!this.mediaElement) return

    if (this.mediaElement instanceof HTMLAudioElement) {
      this.mediaElement.pause()
    } else if (
      this.mediaElement &&
      "pause" in this.mediaElement &&
      typeof (this.mediaElement as { pause?: () => void }).pause === "function"
    ) {
      ;(this.mediaElement as { pause: () => void }).pause()
    }
  }

  private initializeMediaElement() {
    // Create hidden container within controller scope so Stimulus can find it
    this.container = document.createElement("div")
    this.container.style.display = "none"
    this.element.appendChild(this.container)

    if (this.muxPlaybackIdValue && this.hasMuxAudioTemplateTarget) {
      // Clone mux-audio element from template
      const cloned = this.muxAudioTemplateTarget.content.cloneNode(
        true,
      ) as DocumentFragment
      const muxAudio = cloned.querySelector("mux-audio")
      if (muxAudio instanceof HTMLElement) {
        this.container.appendChild(muxAudio)
        this.mediaElement = muxAudio
      }
    } else if (this.srcValue && this.hasAudioTemplateTarget) {
      // Clone HTML5 audio element from template
      const cloned = this.audioTemplateTarget.content.cloneNode(
        true,
      ) as DocumentFragment
      const audio = cloned.querySelector("audio")
      if (audio instanceof HTMLAudioElement) {
        this.container.appendChild(audio)
        this.mediaElement = audio
      }
    }

    if (!this.mediaElement) return

    // Polling will start when playback begins (in play event handler)
    if (!(this.mediaElement instanceof HTMLAudioElement)) {
      // For mux-audio, we'll use polling as backup
    }
  }

  handlePlay() {
    this.state = "playing"
    this.updateButtonState()

    // Track preview play event (only once per preview session)
    if (!this.hasTrackedPreview && this.itemIdValue && this.spaceIdValue) {
      trackPreviewPlay(
        this.itemIdValue,
        this.spaceIdValue,
        this.previewDurationValue,
      )
      this.hasTrackedPreview = true
    }

    // Start time update polling when playback actually starts
    if (!this.timeUpdateIntervalId) {
      this.startTimeUpdate()
    }
  }

  handlePause() {
    if (this.state === "playing") {
      this.state = "idle"
      this.updateButtonState()
      this.stopTimeUpdate()
    }
  }

  handleEnded() {
    // Guard against null mediaElement (can happen if controller was disconnected)
    if (!this.mediaElement) return

    // Skip UI update if already completed (prevents flicker when processTimeUpdate already handled it)
    const wasAlreadyCompleted = this.state === "completed"

    this.state = "completed"

    // Only update UI if not already completed (prevents flicker for HTML5 audio)
    if (!wasAlreadyCompleted) {
      this.updateButtonState()
    }

    this.stopTimeUpdate()
    // Reset volume for both HTML5 audio and mux-audio
    if (this.mediaElement instanceof HTMLAudioElement) {
      this.mediaElement.volume = 1
    } else {
      // For mux-audio, reset volume via media property or directly
      const muxAudio = this.mediaElement as {
        media?: HTMLAudioElement
        volume?: number
      }
      if (muxAudio.media) {
        muxAudio.media.volume = 1
      } else if ("volume" in muxAudio) {
        muxAudio.volume = 1
      }
    }
  }

  handleTimeUpdate() {
    if (!this.mediaElement) return

    if (this.mediaElement instanceof HTMLAudioElement) {
      const currentTime = this.mediaElement.currentTime
      this.processTimeUpdate(currentTime, this.mediaElement)
    } else {
      // For mux-audio, try to get currentTime from media property
      const muxAudio = this.mediaElement as {
        media?: HTMLAudioElement
        currentTime?: number
      }
      const media = muxAudio.media
      if (media) {
        this.processTimeUpdate(media.currentTime, media)
      } else if ("currentTime" in muxAudio) {
        const currentTime = muxAudio.currentTime ?? 0
        this.processTimeUpdate(currentTime, null)
      }
    }
  }

  private processTimeUpdate(
    currentTime: number,
    audioElement: HTMLAudioElement | null,
  ) {
    const fadeStartTime = this.previewDurationValue - 3

    // Update progress ring
    this.updateProgress(currentTime)

    // Update time display
    this.updateTimeDisplay(currentTime)

    // Start fade-out in last 3 seconds and stop at end
    if (currentTime >= fadeStartTime) {
      if (currentTime >= this.previewDurationValue) {
        // Stop playback when reaching preview duration
        this.pause()
        this.state = "completed"
        this.updateButtonState()
        this.stopFadeOut()
        // Reset volume for both HTML5 audio and mux-audio
        if (audioElement) {
          audioElement.volume = 1
        } else if (!(this.mediaElement instanceof HTMLAudioElement)) {
          // For mux-audio, reset volume via media property or directly
          const muxAudio = this.mediaElement as {
            media?: HTMLAudioElement
            volume?: number
          }
          if (muxAudio.media) {
            muxAudio.media.volume = 1
          } else if ("volume" in muxAudio) {
            muxAudio.volume = 1
          }
        }
      } else if (audioElement) {
        // Fade out in last 3 seconds (only for HTML5 audio)
        const fadeProgress = (currentTime - fadeStartTime) / 3
        const volume = Math.max(0, 1 - fadeProgress)
        audioElement.volume = volume
      }
    }
  }

  private updateProgress(currentTime: number) {
    if (!this.hasProgressRingTarget) return

    const progress = Math.min(currentTime / this.previewDurationValue, 1)
    this.progressRingTarget.value = progress * 100
  }

  private updateTimeDisplay(currentTime: number) {
    if (!this.hasCurrentTimeTarget) return

    this.currentTimeTarget.textContent = formatTime(currentTime)
  }

  private startTimeUpdate() {
    // Stop any existing interval first (prevents memory leaks)
    this.stopTimeUpdate()

    // For mux-audio, poll for time updates since timeupdate may not fire reliably
    if (!(this.mediaElement instanceof HTMLAudioElement)) {
      this.timeUpdateIntervalId = setInterval(() => {
        if (!this.mediaElement) {
          this.stopTimeUpdate()
          return
        }

        // Try to get currentTime from mux-audio element
        const muxAudio = this.mediaElement as {
          currentTime?: number
          media?: HTMLAudioElement
          volume?: number
        }
        let currentTime = 0

        if (muxAudio.media) {
          currentTime = muxAudio.media.currentTime
        } else if ("currentTime" in muxAudio) {
          currentTime = muxAudio.currentTime ?? 0
        }

        this.updateProgress(currentTime)
        this.updateTimeDisplay(currentTime)

        // Check if we should stop (at preview duration)
        if (currentTime >= this.previewDurationValue) {
          this.pause()
          this.state = "completed"
          this.updateButtonState()
          this.stopTimeUpdate() // Stop interval when completion detected
          // Reset volume for mux-audio
          if (muxAudio.media) {
            muxAudio.media.volume = 1
          } else if ("volume" in muxAudio) {
            muxAudio.volume = 1
          }
        }
      }, 100) // Update every 100ms
    }
  }

  private stopTimeUpdate() {
    if (this.timeUpdateIntervalId) {
      clearInterval(this.timeUpdateIntervalId)
      this.timeUpdateIntervalId = null
    }
  }

  private stopFadeOut() {
    if (this.fadeIntervalId) {
      clearInterval(this.fadeIntervalId)
      this.fadeIntervalId = null
    }
    this.stopTimeUpdate()
  }

  private updateButtonState() {
    if (this.state === "playing") {
      // Show playing state with circular button and progress
      // The attribute, not the class: the quiet text link's own display
      // utility outranks `hidden`, which left the ring over the word.
      if (this.hasButtonTarget) {
        this.buttonTarget.hidden = true
      }
      if (this.hasPlayingStateTarget) {
        this.playingStateTarget.classList.remove("hidden")
        this.playingStateTarget.classList.add("flex")
      }
      // Show time display below button
      if (this.hasTimeDisplayTarget) {
        this.timeDisplayTarget.classList.remove("hidden")
      }
      // Update pause icon
      if (this.hasPlayPauseIconTarget) {
        this.playPauseIconTarget.setAttribute("name", "pause")
      }
    } else {
      // Show preview button
      if (this.hasButtonTarget) {
        this.buttonTarget.hidden = false
      }
      if (this.hasPlayingStateTarget) {
        this.playingStateTarget.classList.add("hidden")
        this.playingStateTarget.classList.remove("flex")
      }
      // Hide time display
      if (this.hasTimeDisplayTarget) {
        this.timeDisplayTarget.classList.add("hidden")
      }
      // Reset progress ring
      if (this.hasProgressRingTarget) {
        this.progressRingTarget.value = 0
      }
      // Reset time display
      if (this.hasCurrentTimeTarget) {
        this.currentTimeTarget.textContent = "0:00"
      }

      // Update icon for idle/completed states. The icon is a server-rendered
      // <wa-icon>, so swapping `name` keeps its size and library intact.
      if (this.hasIconTarget) {
        this.iconTarget.setAttribute(
          "name",
          this.state === "completed" ? this.replayIconValue : "play",
        )
      }

      // Update text
      if (this.hasTextTarget) {
        if (this.state === "completed") {
          this.textTarget.textContent = "Replay"
        } else {
          this.textTarget.textContent = "Preview"
        }
      }
    }
  }

  private cleanup() {
    this.stopFadeOut()
    this.stopTimeUpdate()
    if (this.mediaElement) {
      this.pause()
    }
    this.container?.remove()
    this.mediaElement = null
    this.container = null
  }
}
